import { motion } from "framer-motion";
import { useLocale } from "../i18n/LocaleContext";

export function AboutSection() {
  const { copy, locale } = useLocale();
  const { about } = copy;
  const isChinese = locale === "zh" || locale === "vi";

  return (
    <section id="about" className="px-5 py-24 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-12 lg:grid-cols-[0.76fr_1.24fr] lg:items-start lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-[0.72rem] uppercase tracking-[0.44em] text-aurora/78">{about.eyebrow}</p>
            <h2
              className={`mt-4 max-w-3xl leading-[1.05] text-[#1f2b31] ${
                isChinese
                  ? "font-sans text-[2.05rem] font-medium tracking-[-0.01em] sm:text-[2.95rem]"
                  : "font-display text-[2.35rem] font-semibold sm:text-[4.1rem]"
              }`}
            >
              {about.title}
            </h2>
            <p className="mt-6 max-w-[18rem] text-[0.9rem] leading-7 text-soft/56">{about.body}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65, delay: 0.06 }}
            className="grid gap-5"
          >
            <div className="relative overflow-hidden rounded-[2.7rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.84),rgba(239,246,249,0.9))] p-3 shadow-[0_14px_28px_rgba(106,138,151,0.06)] backdrop-blur">
              <div className="aspect-[1.55/0.94] overflow-hidden rounded-[2rem] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(242,248,251,0.94))]">
                <img
                  src="/official-brands/roundlab-banner.png"
                  alt="QIANDU Korean beauty portfolio"
                  className="h-full w-full object-contain bg-[rgba(247,250,251,0.92)] p-4 sm:p-5"
                />
              </div>
              <div className="absolute left-7 top-7 rounded-full bg-[rgba(252,255,255,0.72)] px-4 py-2 text-[8px] uppercase tracking-[0.32em] text-soft/54 shadow-[0_8px_18px_rgba(106,138,151,0.06)]">
                Korea HQ
              </div>
            </div>
            <div className="rounded-[2.7rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.84),rgba(239,246,249,0.92))] p-6 shadow-[0_14px_28px_rgba(106,138,151,0.06)] backdrop-blur sm:p-7">
              <div className="grid gap-6 sm:grid-cols-2">
                <div className="rounded-[1.8rem] bg-[rgba(255,255,255,0.6)] p-5 shadow-[0_8px_18px_rgba(106,138,151,0.04)]">
                  <div className="text-[8px] uppercase tracking-[0.34em] text-soft/46">{about.positioningLabel}</div>
                  <div
                    className={`mt-3 leading-tight text-[#1f2b31] sm:text-[1.9rem] ${
                      isChinese ? "font-sans text-[1.36rem] font-medium" : "font-display text-[1.55rem]"
                    }`}
                  >
                    {about.positioningValue}
                  </div>
                </div>
                <div className="rounded-[1.8rem] bg-[rgba(255,255,255,0.6)] p-5 shadow-[0_8px_18px_rgba(106,138,151,0.04)]">
                  <div className="text-[8px] uppercase tracking-[0.34em] text-soft/46">{about.focusLabel}</div>
                  <div
                    className={`mt-3 leading-tight text-[#1f2b31] sm:text-[1.9rem] ${
                      isChinese ? "font-sans text-[1.36rem] font-medium" : "font-display text-[1.55rem]"
                    }`}
                  >
                    {about.focusValue}
                  </div>
                </div>
              </div>
              <div className="mt-5 rounded-[1.95rem] bg-[rgba(255,255,255,0.64)] p-5 shadow-[0_8px_20px_rgba(106,138,151,0.04)] sm:p-6">
                <div className="text-[8px] uppercase tracking-[0.34em] text-soft/46">{about.narrativeLabel}</div>
                <p className="mt-4 max-w-[24rem] text-[0.9rem] leading-7 text-soft/56">{about.narrativeBody}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {about.capabilityBlocks.slice(0, 2).map((item) => (
                <span
                  key={item}
                  className={`rounded-full bg-[rgba(252,255,255,0.78)] px-4 py-2.5 shadow-[0_8px_18px_rgba(106,138,151,0.05)] ${
                    isChinese
                      ? "text-[11px] tracking-[0.08em] text-soft/64"
                      : "text-[10px] uppercase tracking-[0.22em] text-soft/60"
                  }`}
                >
                  {item}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
